import type { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import type { Command } from '@atlaskit/editor-common/types/command';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types/next-editor-plugin';
import { keymap } from '@atlaskit/editor-prosemirror/keymap';

import type { AIPlugin } from '../../aiPluginType';
import type { AIGlobalOptIn } from '../../types/types';
import type { EditorPluginAIConfigItem } from '../../ui/prebuilt/config-items/config-items';
import { createOpenAIModalCommand } from '../decoration/actions';
import { fireAiQuickPromptDismissedEvent, fireAiQuickPromptTriggeredEvent } from './analytics';
import { createHidePromptCommand, pluginKey } from './plugin-factory';
import type { StandDownManager } from './stand-down-manager';

/**
 * Create a separate keymap plugin for the ai-quick-prompt feature.
 * Handles Tab (trigger) and Escape (dismiss) keys.
 *
 * @param options - The options
 * @param options.aiGlobalOptIn - AI opt-in status
 * @param options.api - The editor plugin API
 * @returns A ProseMirror Plugin instance
 */
export function createAiQuickPromptKeymapPlugin({
	aiGlobalOptIn,
	api,
	configItem,
	standDownManager,
}: {
	aiGlobalOptIn: AIGlobalOptIn;
	api: ExtractInjectionAPI<AIPlugin> | undefined;
	configItem: EditorPluginAIConfigItem;
	standDownManager: StandDownManager;
}): SafePlugin {
	const keymapObj: Record<string, Command> = {};

	// --- Tab handler ---
	keymapObj['Tab'] = (state, dispatch, view) => {
		const pluginState = pluginKey.getState(state);
		if (!pluginState?.isPromptVisible || !pluginState.eligibleRange) {
			return false; // Pass through to next plugin
		}

		if (dispatch && view) {
			const range = pluginState.eligibleRange;

			// - Record this node as triggered so the prompt won't show again
			const $from = state.doc.resolve(range.from);
			const parentNode = $from.node($from.depth);
			const nodeId = parentNode.attrs.localId
				? String(parentNode.attrs.localId)
				: `pos:${range.from}`;
			standDownManager.recordTrigger(nodeId);

			// - Hide the prompt
			createHidePromptCommand()(state, dispatch);

			// - Invoke the existing Improve Writing AI action
			const openAIModalCommand = createOpenAIModalCommand({
				state: state,
				configItem,
				lastTriggeredFrom: 'aiQuickPrompt',
				triggerMethod: 'shortcut',
				aiGlobalOptIn,
				overrideFromPos: range.from,
				overrideToPos: range.to,
			});
			openAIModalCommand(state, dispatch);

			// - Fire analytics: triggered
			fireAiQuickPromptTriggeredEvent({ api, experienceName: configItem.key, method: 'tab' });
		}

		return true; // Handled
	};

	// --- Escape handler ---
	keymapObj['Escape'] = (state, dispatch) => {
		const pluginState = pluginKey.getState(state);
		if (!pluginState?.isPromptVisible) {
			return false; // Pass through to next plugin
		}

		if (dispatch) {
			// - Record dismissal for stand-down
			standDownManager.recordDismissal();

			// - Hide the prompt
			createHidePromptCommand()(state, dispatch);

			// - Fire analytics: dismissed
			fireAiQuickPromptDismissedEvent({ api, experienceName: configItem.key, method: 'escape' });
		}

		return true; // Handled
	};

	return keymap(keymapObj) as SafePlugin;
}
